import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import {
  FaHome,
  FaPlaneDeparture,
  FaShieldAlt,
  FaGlobe,
  FaFileAlt,
  FaMapMarkedAlt,
} from 'react-icons/fa';

import { Headercomponent } from '../components/headercomponent';
import NodeBackground from '../components/nodeBackground';
import DemoModal from '../components/demoModal';
import { Footer } from '../components/Footer';

Modal.setAppElement('#root');

const ServiceBadge = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '0.35rem 0.7rem',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: '999px',
      fontSize: '0.8rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      marginBottom: '0.9rem',
      opacity: 0.9,
    }}
  >
    {children}
  </span>
);

const iconMap = {
  'Residential Crime Report': <FaHome />,
  'Travel Risk Brief': <FaPlaneDeparture />,
  'Online Exposure Assessment': <FaShieldAlt />,
  'OSINT Risk Report': <FaGlobe />,
  'Venue and Event Assessment': <FaMapMarkedAlt />,
  'Custom Intelligence Product': <FaFileAlt />,
};

const ServiceGraphic = ({ title, items, onItemClick }) => (
  <div className="service-graphic">
    <div className="service-graphic-header">
      <h3>{title}</h3>
    </div>

    <div className="service-graphic-grid">
      {items.map((item) => (
        <button
          key={item}
          type="button"
          className="service-graphic-item"
          onClick={() => onItemClick?.(item)}
        >
          <div className="service-graphic-left">
            <span className="service-graphic-icon">
              {iconMap[item] || <FaFileAlt />}
            </span>
            <span>{item}</span>
          </div>

          <span className="service-arrow">→</span>
        </button>
      ))}
    </div>
  </div>
);

function IntelligenceProducts() {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [productModal, setProductModal] = useState(null);

  const handleModal = () => setModalIsOpen((current) => !current);

  const productDetails = {
    'Residential Crime Report': {
      title: 'Residential Crime Intelligence Report',
      need:
        'Families, advisors, and real estate professionals often need to understand whether an address or neighborhood creates avoidable risk before a purchase, lease, relocation, or recommendation.',
      includes: [
        'Property-centered crime mapping',
        'Historical crime trends near the address',
        'Violent, property, theft, and vehicle-related indicators',
        'Neighborhood and surrounding-area context',
        'Plain-language analyst assessment',
      ],
      for:
        'Home buyers, family offices, real estate advisors, relocation clients, and security teams evaluating residential decisions.',
    },
    'Travel Risk Brief': {
      title: 'Travel Risk Intelligence Brief',
      need:
        'Executives, families, and protective teams need practical intelligence before staying, meeting, moving, or operating in an unfamiliar area.',
      includes: [
        'Destination and hotel-area review',
        'Crime patterns near key locations',
        'Arrival, parking, movement, and venue-area considerations',
        'Relevant public safety or disruption indicators',
        'Planning recommendations tied to observed findings',
      ],
      for:
        'Executive assistants, chiefs of staff, family offices, executive protection teams, and private travelers.',
    },
    'Online Exposure Assessment': {
      title: 'Online Exposure Assessment',
      need:
        'Publicly available information about executives, family members, residences, and organizations can create personal, operational, or reputational concerns.',
      includes: [
        'Public profile and people-search exposure review',
        'Address, contact, household, and family exposure indicators',
        'Social media and public-facing risk notes',
        'Mitigation-oriented recommendations',
        'Executive-ready exposure summary',
      ],
      for:
        'Executives, founders, public-facing individuals, family offices, and protective intelligence teams.',
    },
    'OSINT Risk Report': {
      title: 'OSINT Risk Intelligence Report',
      need:
        'Some client questions require analyst-led open-source collection, source review, and judgment rather than a standard report template.',
      includes: [
        'Defined intelligence question and scope',
        'Open-source collection and source context',
        'Findings, risk indicators, and intelligence gaps',
        'Analyst judgment and recommended next steps',
        'Executive-ready written assessment',
      ],
      for:
        'Security teams, attorneys, investigators, family offices, corporate teams, and private clients with specific intelligence questions.',
    },
    'Venue and Event Assessment': {
      title: 'Venue and Event Intelligence Assessment',
      need:
        'Events create temporary visibility and concentrated exposure. Teams need to understand the venue area, public safety context, and relevant online indicators before people arrive.',
      includes: [
        'Venue and surrounding-area review',
        'Nearby crime and safety context',
        'Arrival, departure, access, and parking considerations',
        'Online attention, protest, or disruption indicators',
        'Event-focused recommendations',
      ],
      for:
        'Event planners, corporate teams, executive protection details, private event hosts, and high-visibility organizations.',
    },
    'Custom Intelligence Product': {
      title: 'Custom Intelligence Product',
      need:
        'Not every concern fits a standard product. Custom work is built around the client’s specific decision, location, person, organization, or open-source question.',
      includes: [
        'Custom intelligence question and scope refinement',
        'Open-source, crime, location, exposure, or online-risk analysis as needed',
        'Custom-coded workflows, maps, timelines, or visuals where useful',
        'Findings and intelligence gaps',
        'Recommended next steps',
      ],
      for:
        'Family offices, executive staff, attorneys, investigators, security teams, and private clients with unique intelligence needs.',
    },
  };

  const products = [
    {
      badge: 'Property Intelligence',
      title: 'Residential Crime Intelligence Reports',
      text:
        'A residential crime report helps clients understand what reported crime around a specific address actually means. Weathervane uses mapped crime data, historical patterns, nearby incident concentration, and analyst commentary to support property, relocation, and neighborhood decisions.',
      graphicTitle: 'Residential Deliverable',
      items: ['Residential Crime Report', 'Custom Intelligence Product'],
    },
    {
      badge: 'Executive Travel',
      title: 'Travel Risk Intelligence Briefs',
      text:
        'Travel risk briefs support executive movement, hotel selection, venue review, and destination planning. The product translates public safety data, location context, and open-source indicators into clear planning considerations for staff and protective teams.',
      graphicTitle: 'Travel Deliverable',
      items: ['Travel Risk Brief', 'Venue and Event Assessment'],
    },
    {
      badge: 'Exposure Review',
      title: 'Online Exposure Assessments',
      text:
        'Online exposure assessments identify publicly available information that may create protective, reputational, or operational concerns. The product is designed to help executives, families, and staff understand what is visible and what can be reduced.',
      graphicTitle: 'Exposure Deliverable',
      items: ['Online Exposure Assessment', 'OSINT Risk Report'],
    },
    {
      badge: 'Open-Source Intelligence',
      title: 'OSINT Risk Intelligence Reports',
      text:
        'OSINT reports are built around a defined intelligence question. Weathervane collects, reviews, and structures public information, then provides findings, source context, intelligence gaps, and analyst judgment in an executive-ready format.',
      graphicTitle: 'OSINT Deliverable',
      items: ['OSINT Risk Report', 'Custom Intelligence Product'],
    },
    {
      badge: 'Events and Venues',
      title: 'Venue and Event Intelligence Assessments',
      text:
        'Venue and event assessments help clients understand the surrounding environment before a meeting, appearance, fundraiser, conference, or private event. The deliverable can include area crime patterns, movement considerations, online indicators, and planning recommendations.',
      graphicTitle: 'Event Deliverable',
      items: ['Venue and Event Assessment', 'Travel Risk Brief'],
    },
    {
      badge: 'Custom Intelligence',
      title: 'Custom Intelligence Products',
      text:
        'Some questions require a product built from scratch. Weathervane can combine analyst research, custom-coded data workflows, crime mapping, exposure review, OSINT collection, timelines, and visuals to answer the client’s specific question.',
      graphicTitle: 'Custom Deliverable',
      items: ['Custom Intelligence Product', 'OSINT Risk Report'],
    },
  ];

  return (
    <>
      <Headercomponent />

      <div className="container">
        <div className="hero">
          <NodeBackground />

          <div className="hero-content">
            <h1>Intelligence Products Built Around the Decision</h1>
            <p>
              Discreet intelligence reports, assessments, briefs, maps, and custom OSINT products for
              family offices, executive protection teams, private clients, residential decisions, travel
              planning, events, and sensitive open-source concerns.
            </p>

            <button className="cta-button" onClick={handleModal}>
              Discuss a Private Assessment
            </button>

            <div className="hero-proof-row">
              <span>BLUF summaries</span>
              <span>Maps and visuals</span>
              <span>Analyst commentary</span>
              <span>Decision-focused findings</span>
            </div>
          </div>

          <div className="hero-diagram">
            <ServiceGraphic
              title="Representative Products"
              items={[
                'Residential Crime Report',
                'Travel Risk Brief',
                'Online Exposure Assessment',
                'OSINT Risk Report',
                'Venue and Event Assessment',
                'Custom Intelligence Product',
              ]}
              onItemClick={(item) => {
                if (productDetails[item]) {
                  setProductModal(productDetails[item]);
                }
              }}
            />
          </div>
        </div>

        <section className="platform-overview">
          <h2>Not generic reports. Intelligence products scoped around the question.</h2>
          <p>
            Weathervane products are designed for people who need to make a decision before they have perfect
            information. Each product starts with the intelligence question, then combines research, data
            review, mapping, custom-coded workflows, source context, and analyst judgment into a deliverable
            that can be used by executives, staff, advisors, and protective teams.
          </p>

          <div className="overview-cards">
            {products.map((product, index) => (
              <div
                className={`overview-feature ${
                  index === 1 || index === 3 || index === 5 ? 'overview-feature-reverse' : ''
                }`}
                key={product.title}
              >
                <div className="feature-graphic">
                  <ServiceGraphic
                    title={product.graphicTitle}
                    items={product.items}
                    onItemClick={(item) => {
                      if (productDetails[item]) {
                        setProductModal(productDetails[item]);
                      }
                    }}
                  />
                </div>

                <div className="feature-text">
                  <ServiceBadge>{product.badge}</ServiceBadge>
                  <h3>{product.title}</h3>
                  <p>{product.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="process-deliverables-section">
          <div className="process-column">
            <span className="section-kicker">Product Methodology</span>
            <h2>From Intelligence Question to Deliverable</h2>
            <p>
              Every product starts with the decision the client needs to make. We define the scope, collect
              and structure relevant information, analyze what matters, and produce a usable intelligence
              product for the client’s situation.
            </p>

            <div className="process-steps">
              <div className="process-step">
                <span>01</span>
                <div>
                  <h3>Define the Question</h3>
                  <p>
                    We clarify the address, destination, executive, family member, venue, organization,
                    event, or concern that needs to be assessed.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>02</span>
                <div>
                  <h3>Collect and Structure</h3>
                  <p>
                    We gather relevant public data, open-source information, crime records, exposure
                    indicators, location context, and source material.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>03</span>
                <div>
                  <h3>Analyze What Matters</h3>
                  <p>
                    Analyst review separates raw information from findings that may affect protective,
                    residential, travel, reputational, or operational decisions.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>04</span>
                <div>
                  <h3>Deliver the Product</h3>
                  <p>
                    Clients receive a clear intelligence product with visuals, BLUF-style commentary,
                    findings, gaps, and recommendations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="deliverables-column">
            <span className="section-kicker">What You Receive</span>
            <h2>Executive-Ready Intelligence Products</h2>
            <p>
              Each deliverable is built to be clear enough for non-technical decision makers and detailed
              enough to support staff, advisors, analysts, and security professionals.
            </p>

            <div className="deliverables-grid">
              <div className="deliverable-card">
                <h3>BLUF Summary</h3>
                <p>A concise bottom-line assessment of the most important findings.</p>
              </div>

              <div className="deliverable-card">
                <h3>Maps and Visuals</h3>
                <p>Crime maps, exposure visuals, timelines, charts, and other briefing graphics.</p>
              </div>

              <div className="deliverable-card">
                <h3>Analyst Findings</h3>
                <p>Clear explanation of what was identified, why it matters, and what it supports.</p>
              </div>

              <div className="deliverable-card">
                <h3>Intelligence Gaps</h3>
                <p>Transparent explanation of what is unknown or not supported by the available information.</p>
              </div>

              <div className="deliverable-card">
                <h3>Custom Analysis</h3>
                <p>Custom-coded data workflows, mapping, timelines, or triage logic when useful.</p>
              </div>

              <div className="deliverable-card">
                <h3>Recommendations</h3>
                <p>Practical considerations tied directly to the findings and client decision.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Complete representative work is shared through a controlled review process.</h2>
          <p>
            Because many assessments involve sensitive locations, individuals, travel plans, and protective
            considerations, full examples are shared during consultations or upon request.
          </p>

          <button className="cta-button" onClick={handleModal}>
            Request Representative Work
          </button>
        </section>

        <section className="cta-section">
          <h2>Bring the question. We will build the intelligence product around it.</h2>
          <p>
            Request a product for a property, travel destination, executive, family member, event,
            organization, venue, or open-source intelligence concern.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
            <button className="cta-button" onClick={handleModal}>
              Discuss a Private Assessment
            </button>

            <Link className="cta-button" to="/services">
              View Services
            </Link>
          </div>
        </section>

        <DemoModal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} />

        <Modal
          isOpen={!!productModal}
          onRequestClose={() => setProductModal(null)}
          className="service-detail-modal"
          overlayClassName="report-preview-overlay"
        >
          {productModal && (
            <>
              <div className="report-preview-header">
                <h3>{productModal.title}</h3>

                <button
                  type="button"
                  className="report-preview-close"
                  onClick={() => setProductModal(null)}
                >
                  ×
                </button>
              </div>

              <div className="service-detail-content">
                <section>
                  <h4>Why It Matters</h4>
                  <p>{productModal.need}</p>
                </section>

                <section>
                  <h4>What This Includes</h4>
                  <ul>
                    {productModal.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h4>Who It Is For</h4>
                  <p>{productModal.for}</p>
                </section>
              </div>
            </>
          )}
        </Modal>
      </div>
      <Footer/>
    </>
  );
}

export default IntelligenceProducts;